import { useQuery } from "@tanstack/react-query";

import type {
    BillingEntitlement,
    BillingFeatureUsageSummary,
} from "~/features/billing/types";
import { useApiClient } from "~/hooks/use-api-client";

interface BillingUpgradeContextResponse {
    enforced: boolean;
    entitlement: BillingEntitlement | null;
    policy: {
        isTrial: boolean;
        metricType: "session" | "client";
        monthlyLimitUnits: number;
        cycleType: "subscription_anniversary";
        tierName: "starter" | "integration" | "growth" | null;
    } | null;
    featureUsage: BillingFeatureUsageSummary | null;
}

export function useBillingUpgradeContext(options?: { enabled?: boolean }) {
    const apiClient = useApiClient();
    return useQuery({
        queryKey: ["billing", "upgrade-context"],
        queryFn: async () => {
            const response = await apiClient.get("/billing/upgrade-context");
            if (response.status === 401) {
                return {
                    enforced: false,
                    entitlement: null,
                    policy: null,
                    featureUsage: null,
                } satisfies BillingUpgradeContextResponse;
            }

            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                throw new Error(
                    typeof data?.error === "string"
                        ? data.error
                        : "Failed to load billing upgrade context",
                );
            }

            return (await response.json()) as BillingUpgradeContextResponse;
        },
        staleTime: 60_000,
        enabled: options?.enabled ?? true,
    });
}
